import { Dropdown, PrimaryButton, Stack, TextField } from "@fluentui/react";
import * as React from "react";
import { generateOrUpdateCustomerReport } from "../../../services/customers/CustomerService";
import { Loading } from "../../../services/Loading";
import { ICustomerReport, ITokenUser } from "../../../vm";
import {
  childrenGap,
  FILE_UPLOAD_CONFIG,
  FILE_UPLOAD_MODULES,
} from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { ToastContext } from "../../common/ToastProvider";
import {
  getFileTypeErrorMessage,
  uploadReportFile,
  validateFileType,
} from "../../../services/UtilService";

const dropDownOptionsForCEM = [
  { key: 0, text: "Use last generated report and submit" },
  // { key: 1, text: "Generate new report and submit" },
  { key: 2, text: "Upload new report and submit" },
  { key: 3, text: "Reject Report" },
];
const dropDownOptionsForOpTeam = [
  { key: 0, text: "Use last generated report and submit" },
  { key: 1, text: "Generate new report and submit" },
  // { key: 2, text: 'Upload new report and submit' },
  //   { key: 3, text: 'Reject Report' },
];
const dropDownOptionsWithReject = [
  { key: 0, text: "Use last generated report and approve" },
  { key: 1, text: "Generate new report and approve" },
  // { key: 2, text: 'Upload new report and approve' },
  { key: 3, text: "Reject Report" },
];
const dropDownOptionsForGoBack = [
  { key: 4, text: "Cancel approval & go back to previous step" },
];

export interface ManageReportApprovalProps {
  customerReport: ICustomerReport;
  handleClose: Function;
  userTokenData: ITokenUser;
  isGoBackAtFinalStep?: boolean;
}

const ManageReportApproval: React.SFC<ManageReportApprovalProps> = ({
  customerReport,
  handleClose,
  userTokenData,
  isGoBackAtFinalStep,
}) => {
  //custom hooks
  const { showToast } = React.useContext(ToastContext);

  // file upload config
  const fileUploadConfig =
    FILE_UPLOAD_CONFIG[FILE_UPLOAD_MODULES.MANAGE_REPORT_APPROVAL];

  // local state
  const [isLoading, setLoading] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(
    isGoBackAtFinalStep ? 4 : 0
  );
  // const [selectedOption, setSelectedOption] = React.useState(0);
  const [remarks, setRemarks] = React.useState("");
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null); // NEW
  const [currentDcStatus, setCurrentDcStatus] = React.useState({
    datacenterId: "",
    status: 0,
  });
  //   const [cReport, setCReport] = React.useState(customerReport);

  React.useEffect(() => {
    if (userTokenData.isAdmin && [0, 2, 5].includes(userTokenData.role)) {
      let maxNumber = 0;
      customerReport.datacenterStatus.forEach((d) => {
        // if (d.status > maxNumber) {
        maxNumber = d.status;
        // }
      });
      setCurrentDcStatus({ ...currentDcStatus, status: maxNumber });
    } else {
      const foundDC = customerReport.datacenterStatus.find((d) =>
        userTokenData.datacenterIds.includes(d.datacenterId)
      );
      if (foundDC) {
        setCurrentDcStatus(foundDC);
      }
    }
  }, []);

  const handleDialogClose = (isOpen: boolean, data?: ICustomerReport) => {
    handleClose(isOpen, data);
  };

  const approveReport = async () => {
    if (selectedOption === 4 && !remarks) return;

    // File validation
    if (selectedOption === 2) {
      if (!file) {
        setFileError("File is required");
        return;
      }

      const isValidType = validateFileType(
        file,
        fileUploadConfig.acceptedFileTypes
      );
      if (!isValidType) {
        setFileError(
          getFileTypeErrorMessage(fileUploadConfig.fileTypeDescription)
        );
        return;
      }
    }

    setLoading(true);
    // todo update report
    const tempCR = customerReport;
    // generate new copy
    if (selectedOption === 1) {
      tempCR.isGenerateNewReport = true;
    }

    // report status
    if (selectedOption === 4) {
      tempCR.isGoBackAtFinalStep = true;
    } else if (selectedOption !== 3) {
      tempCR.isApproved = true;
    } else {
      tempCR.isRejected = true;
    }
    tempCR.remarks = remarks;

    if (selectedOption === 2) {
      if (!file) return showToast("Please select report", "error");
      const fileName = customerReport.customerId;
      const uploadedFileName = await uploadReport(fileName, file);
      if (uploadedFileName) tempCR.file = uploadedFileName;
      else return;
    }

    const result = await generateOrUpdateCustomerReport(tempCR);
    if (result?.data) {
      const tempCustomerReport: ICustomerReport = result.data;
      //   setCReport(tempCustomerReport);
      handleDialogClose(false, tempCustomerReport);
      showToast("Customer report generated / updated successfully", "success");
    } else {
      showToast(
        result?.message || "Error while generating / updating customer report",
        "error"
      );
      // return setLoading(false);
    }
    setLoading(false);
  };

  const uploadReport = async (fileName: string, file: File) => {
    // let result = await getSignedUrl([fileName], file.type);
    // let a={...file as any}
    // a["name"]=fileName;
    let result = await uploadReportFile(file, fileName);

    if (result.success) {
      return result?.data?.fileName || "";
      // return fileName;
    } else {
      showToast(result.message || `Error while uploading image`, "error");
    }
    return undefined;
  };

  const handleFileChange = (files: any) => {
    setFileError(null); // Reset error first

    if (files && files.length > 0) {
      const isValidType = validateFileType(
        files[0],
        fileUploadConfig.acceptedFileTypes
      );

      if (!isValidType) {
        const errorMessage = getFileTypeErrorMessage(
          fileUploadConfig.fileTypeDescription
        );
        showToast(errorMessage, "error");
        setFileError(errorMessage);
      } else {
        setFileError(null);
      }

      setFile(files[0]);
    } else {
      setFile(null);
      showToast("Select only valid file", "error");
    }
  };
  const status = currentDcStatus.status;
  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <CustomDrawer
        title={"Approve Report"}
        handelOpen={true}
        onClose={() => handleDialogClose(false)}
      >
        <Stack tokens={childrenGap}>
          <Stack.Item className="custom-dropdown mb-30">
            <Dropdown
              // options={dropDownOptionsForOpTeam}
              options={
                status === 0 || status === -2
                  ? dropDownOptionsForOpTeam
                  : status === 1 || status === -3
                  ? dropDownOptionsWithReject
                  : status === 4 || status === 5
                  ? dropDownOptionsForGoBack
                  : dropDownOptionsForCEM
              }
              selectedKey={selectedOption}
              onChange={(e, item): void => {
                // setFieldValue('role', item?.key);
                // console.log(item?.key);
                setRemarks("");
                setFile(null);
                setFileError(null);
                setSelectedOption(item?.key as number);
              }}
            />
          </Stack.Item>
          {selectedOption === 2 && (
            <Stack.Item
              className="custom-textfield"
              styles={{ root: { marginBottom: 16 } }}
            >
              <TextField
                name="file"
                type="file"
                accept={fileUploadConfig.acceptedFileTypes}
                onChange={(event: any) => {
                  handleFileChange(event.target.files);
                }}
                //   onBlur={handleBlur}
                label="Select a file"
                errorMessage={fileError || ""}
                description={fileUploadConfig.hintText}
                className="file-selector"
              />
            </Stack.Item>
          )}

          {/* {selectedOption === 3 && ( */}
          <Stack.Item
            className="custom-textfield"
            styles={{ root: { marginBottom: 16 } }}
          >
            <TextField
              name="remarks"
              onChange={(e: any, newValue: any) => {
                setRemarks(newValue);
              }}
              //   onBlur={handleBlur}
              value={remarks}
              label="Remarks"
              errorMessage={selectedOption === 4 && !remarks ? "required" : ""}
            />
          </Stack.Item>
          {/* )} */}
          <Stack.Item align="end">
            <PrimaryButton
              text="Proceed"
              onClick={() => {
                approveReport();
              }}
            />
          </Stack.Item>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageReportApproval;
